﻿import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import {
  ShepDataGridPro,
  ShepCheckbox,
  ShepPagination,
} from "@/components/GenericComponents/ShepComponents";
import {
  fetchTemplates,
  selectSchemaCollectionRecords,
  setSelectedTemplateInfo,
} from "@/features/connections/integrationSlice";
import { useDispatch, useSelector } from "react-redux";
import { SearchIcon } from "@/ImageRepo";

const LoadCollectionGrid = () => {
  const [templates, setTemplates] = useState([]);
  const dispatch = useDispatch();
  const [selectedRow, setSelectedRow] = useState(null);

  const [searchFilter, setSearchFilter] = useState("");

  const columns = [
    {
      field: "id",
      headerName: "Template Id",
      width: 200,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
    {
      field: "TradingPartnerName",
      headerName: "Template Name",
      width: 300,
    },
    {
      field: "ISAId",
      headerName: "Interchange Id",
      width: 200,
    },
    {
      field: "TISAId",
      headerName: "Interchange Id - Test",
      width: 200,
    },
    {
      field: "GS03",
      headerName: "Application Code",
      width: 200,
    },
    {
      field: "TGS03",
      headerName: "Application Code - Test",
      width: 200,
    },
  ];

  const handleTextChange = (event) => {
    setSearchFilter(event.target.value);
    if (event.target.value === "") {
      dispatch(fetchTemplates({ filter: "" }));
    }
  };

  useEffect(() => {
    dispatch(fetchTemplates({ filter: "" }));
  }, []);

  const rows = useSelector(selectSchemaCollectionRecords);

  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const handleRowSelection = (newRowSelectionModel) => {
    if (newRowSelectionModel.length > 1) {
      //to support single row selection, since the combination of disableMultipleRowSelection={true}
      // and checkboxSelection={true} does not work since checkboxSelection={true} overrides the "disableMultipleRowSelection"
      newRowSelectionModel = [newRowSelectionModel.pop()];
      setRowSelectionModel(newRowSelectionModel);
    } else {
      setRowSelectionModel(newRowSelectionModel);
    }

    const selectedData = rows.find((row) => row.id === newRowSelectionModel[0]);

    if (selectedData !== null) {
      dispatch(setSelectedTemplateInfo({ selectedTemplateInfo: selectedData }));
    } else {
      dispatch(setSelectedTemplateInfo({ selectedTemplateInfo: [] }));
    }
  };

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
        }}
      >
        <TextField
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "var(--text-icon-regular)" }} />
              </InputAdornment>
            ),
          }}
          placeholder={"Search by Template Name or Template Id"}
          value={searchFilter}
          onChange={handleTextChange}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.defaultPrevented = true; // Prevent's default 'Enter' behavior.
              dispatch(fetchTemplates({ filter: searchFilter }));
            }
          }}
          sx={{
            width: "450px",
            marginBottom: "20px",
            "&:hover fieldset": {
              backgroundColor: "#ffffff",
            },
            "&.MuiFormControl-root .MuiInputBase-root": {
              borderRadius: "5px",
            },
            "&:hover fieldset": {
              color: "#000000",
            },
          }}
        />
        <Box
          sx={{
            height: "78px",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            padding: "20px 0px 20px 53px",
            borderRadius: "4px",
            border: "1px black",
            boxSizing: "border-box",
          }}
        >
          <Typography
            style={{
              fontSize: "var(--font-lg)",
              fontWeight: "var(--font-semi-bold)",
            }}
          >
            Schema Collection
          </Typography>
        </Box>
        <Box sx={{ marginBottom: "50px" }}>
          <ShepDataGridPro
            isDataLoaded={true}
            secondsToStall={20}
            rows={rows}
            columns={columns}
            columnHeaderHeight={80}
            rowHeight={80}
            pagination
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5, page: 0 },
              },
            }}
            whiteHeader={true}
            checkboxSelection={true}
            disableMultipleRowSelection={false}
            disableColumnMenu={true}
            disableColumnFilter={true}
            disableColumnReorder={true}
            hideFooterSelectedRowCount={true}
            disableRowSelectionOnClick={true}
            onRowSelectionModelChange={handleRowSelection}
            rowSelectionModel={rowSelectionModel}
            slots={{
              baseCheckbox: ShepCheckbox,
              pagination: ShepPagination,
            }}
            sx={{
              color: "var(--text-primary)",
              fontSize: "var(--font-sm)",
              fontFamily: "var(--font-family)",
              borderTopLeftRadius: "44px",
              borderTopRightRadius: "44px",
              backgroundColor: "var(--default-light)",
              border: "none",
              "& .MuiDataGrid:hover": {
                backgroundColor: "inherit",
              },
              "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                opacity: 0,
                borderBottom: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "var(--tertiary-13)",
              },
              "& .Mui-selected": {
                backgroundColor: "var(--primary-pale)",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                {
                  outline: "none",
                },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                {
                  opacity: 1,
                },
              "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                opacity: 1,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "var(--font-md)",
                fontWeight: "var(--font-bold)",
                borderLeft: "none",
              },
              "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                {
                  borderBottomLeftRadius: "4px",
                  borderBottomRightRadius: "4px",
                  backgroundColor: "var(--primary-pale)",
                  borderBottom: "none",
                  borderLeft: "none",
                },
              "& .MuiDataGrid-columnHeader[data-field='connection']": {
                marginLeft: 0,
              },
              "& .MuiDataGrid-cell[data-field='connection']": {
                marginLeft: 0,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LoadCollectionGrid;
